import React, { useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import {
    Popover,
    Typography,
    Button,
    MenuItem,
    Box,
    CircularProgress,
    makeStyles,
    Theme,
} from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'

import { CompanyStatus } from '../../../../../models'
import {
    BulkUpdatePropertyStatusResponse,
    CompanyWithInvoiceCode,
} from '../../types'
import {
    EDITABLE_STATUS_OPTIONS,
    formatStatusLabel,
    getStatusBreakdown,
    getStatusDotColor,
} from '../../utils/statusUtils'

const useStyles = makeStyles((theme: Theme) => ({
    content: {
        padding: theme.spacing(2),
        minWidth: 280,
        maxWidth: 360,
    },
    breakdownList: {
        margin: theme.spacing(1, 0, 2),
        padding: 0,
        listStyle: 'none',
    },
    breakdownItem: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(0.5),
    },
    statusDot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        flexShrink: 0,
        marginRight: theme.spacing(1),
    },
    targetSection: {
        marginBottom: theme.spacing(2),
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: theme.spacing(1),
    },
    actionButton: {
        marginLeft: theme.spacing(1),
    },
    statusOptionLabel: {
        display: 'inline-flex',
        alignItems: 'center',
    },
}))

interface BulkUpdateStatusPopoverProps {
    anchorEl: HTMLElement | null
    open: boolean
    onClose: () => void
    selectedProperties: CompanyWithInvoiceCode[]
    onApply: (
        propertyIds: number[],
        status: CompanyStatus,
    ) => Promise<BulkUpdatePropertyStatusResponse>
    onSuccess?: (status: CompanyStatus) => void
}

export const BulkUpdateStatusPopover: React.FC<BulkUpdateStatusPopoverProps> = ({
    anchorEl,
    open,
    onClose,
    selectedProperties,
    onApply,
    onSuccess,
}) => {
    const classes = useStyles()
    const theme = useTheme()
    const [targetStatus, setTargetStatus] = useState<CompanyStatus | null>(null)
    const [isApplying, setIsApplying] = useState(false)

    const breakdown = useMemo(() => getStatusBreakdown(selectedProperties), [
        selectedProperties,
    ])

    const selectedCount = selectedProperties.length
    const alreadyAtTargetCount = targetStatus
        ? selectedProperties.filter((p) => p.status === targetStatus).length
        : 0
    const allAlreadyAtTarget =
        targetStatus !== null && alreadyAtTargetCount === selectedCount

    const handleClose = () => {
        setTargetStatus(null)
        onClose()
    }

    const handleApply = async () => {
        if (!targetStatus || allAlreadyAtTarget) {
            return
        }

        setIsApplying(true)
        try {
            const propertyIds = selectedProperties.map((p) => p.id)
            const result = await onApply(propertyIds, targetStatus)

            const targetLabel = formatStatusLabel(targetStatus)
            let message = `Updated ${result.updated_count} propert${
                result.updated_count === 1 ? 'y' : 'ies'
            } to ${targetLabel}`
            if (result.skipped_count > 0) {
                message += ` (${result.skipped_count} already ${targetLabel})`
            }
            if (result.not_found_ids.length > 0) {
                message += ` (${result.not_found_ids.length} not found)`
            }
            toast.success(message)

            onSuccess?.(targetStatus)
            setTargetStatus(null)
            onClose()
        } catch (e: any) {
            toast.error(
                `Failed to update status: ${
                    e?.response?.data?.error ||
                    e?.response?.data?.detail ||
                    'Unknown error'
                }`,
                { autoClose: 5000 },
            )
        } finally {
            setIsApplying(false)
        }
    }

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
        >
            <Box className={classes.content}>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    {selectedCount} propert{selectedCount === 1 ? 'y' : 'ies'}{' '}
                    selected
                </Typography>

                <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ display: 'block', marginTop: 4 }}
                >
                    Current statuses
                </Typography>
                <ul className={classes.breakdownList}>
                    {breakdown.map(({ status, count }) => (
                        <li key={status} className={classes.breakdownItem}>
                            <span
                                className={classes.statusDot}
                                style={{
                                    backgroundColor: getStatusDotColor(
                                        status,
                                        theme,
                                    ),
                                }}
                            />
                            <Typography variant="body2">
                                {count} {formatStatusLabel(status)}
                            </Typography>
                        </li>
                    ))}
                </ul>

                <div className={classes.targetSection}>
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ display: 'block', marginBottom: 4 }}
                    >
                        Change all to
                    </Typography>
                    {EDITABLE_STATUS_OPTIONS.map((option) => (
                        <MenuItem
                            key={option}
                            selected={option === targetStatus}
                            onClick={() => setTargetStatus(option)}
                        >
                            <span className={classes.statusOptionLabel}>
                                <span
                                    className={classes.statusDot}
                                    style={{
                                        backgroundColor: getStatusDotColor(
                                            option,
                                            theme,
                                        ),
                                    }}
                                />
                                {formatStatusLabel(option)}
                            </span>
                        </MenuItem>
                    ))}
                </div>

                {allAlreadyAtTarget && (
                    <Typography variant="caption" color="textSecondary">
                        All selected properties are already{' '}
                        {formatStatusLabel(targetStatus!)}
                    </Typography>
                )}

                <div className={classes.actions}>
                    <Button
                        size="small"
                        onClick={handleClose}
                        disabled={isApplying}
                    >
                        Cancel
                    </Button>
                    <Button
                        size="small"
                        color="primary"
                        variant="contained"
                        className={classes.actionButton}
                        onClick={handleApply}
                        disabled={
                            !targetStatus || allAlreadyAtTarget || isApplying
                        }
                    >
                        {isApplying ? (
                            <CircularProgress size={18} color="inherit" />
                        ) : (
                            'Apply'
                        )}
                    </Button>
                </div>
            </Box>
        </Popover>
    )
}
